import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { adminGet, adminPost } from "@/lib/admin-api";

interface QueueItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  source: string;
  authorSlug: string;
  author: { name: string };
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  items: QueueItem[];
}

function VoicesQueueInner() {
  const [items, setItems] = React.useState<QueueItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const res = await adminGet<ListResponse>(
        "/api/admin/voices/posts/list?status=pending_review"
      );
      setItems(res.items);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const approve = async (id: string) => {
    setBusyId(id);
    try {
      await adminPost("/api/admin/voices/posts/approve", { id });
      await load();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id: string) => {
    const reason = window.prompt("Reason for rejection (sent to the author):");
    if (!reason || !reason.trim()) return;
    setBusyId(id);
    try {
      await adminPost("/api/admin/voices/posts/reject", { id, reason: reason.trim() });
      await load();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusyId(null);
    }
  };

  const triggerSync = async () => {
    setBusyId("sync");
    try {
      const res = await adminPost<{ ok: true; summary: Record<string, unknown> }>(
        "/api/admin/voices/inbound/sync"
      );
      await load();
      window.alert(
        `Inbound sync complete:\n${JSON.stringify(res.summary, null, 2)}`
      );
    } catch (err) {
      setError(String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageShell title="Review queue" status="admin">
      <Seo title="Voices review queue — admin" description="" path="/admin/voices/queue" noindex />
      <div className="mt-8">
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <Link
            to="/admin/voices"
            className="font-mono text-xs uppercase tracking-widest text-ink-2 underline hover:text-ink"
          >
            ← all posts
          </Link>
          <button
            type="button"
            onClick={triggerSync}
            disabled={busyId === "sync"}
            className="ml-auto border border-hairline font-mono text-xs uppercase tracking-widest px-4 py-2 hover:bg-paper-2 disabled:opacity-50"
          >
            {busyId === "sync" ? "Syncing…" : "Pull inbox now"}
          </button>
        </div>

        {error && (
          <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-6">
            {error}
          </div>
        )}

        {items === null && !error && (
          <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">Loading…</p>
        )}

        {items && items.length === 0 && (
          <p className="font-serif text-base text-ink-2 leading-relaxed">
            Queue is empty.
          </p>
        )}

        {items && items.length > 0 && (
          <ul className="divide-y divide-hairline border-t border-b border-hairline">
            {items.map((item) => (
              <li key={item.id} className="py-6">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                  <span className="font-mono text-[10px] text-amber-600 uppercase tracking-widest">
                    Pending
                  </span>
                  <span className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                    {item.source}
                  </span>
                  <span className="font-mono text-[10px] text-ink-3">
                    {new Date(item.updatedAt).toLocaleString("en-GB")}
                  </span>
                  <span className="ml-auto font-mono text-xs text-ink-3">
                    by {item.author?.name ?? item.authorSlug}
                  </span>
                </div>
                <h2 className="font-serif text-xl text-ink mb-1">
                  <Link
                    to={`/admin/voices/${item.id}`}
                    className="hover:text-claude transition-colors"
                  >
                    {item.title || "(untitled)"}
                  </Link>
                </h2>
                {item.excerpt && (
                  <p className="font-serif text-sm text-ink-2 leading-relaxed mb-3 line-clamp-3">
                    {item.excerpt}
                  </p>
                )}
                <div className="flex flex-wrap gap-3 mt-3">
                  <button
                    type="button"
                    onClick={() => approve(item.id)}
                    disabled={busyId === item.id}
                    className="bg-emerald-700 text-paper font-mono text-xs uppercase tracking-widest px-4 py-2 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => reject(item.id)}
                    disabled={busyId === item.id}
                    className="border border-red-500/40 text-red-500 font-mono text-xs uppercase tracking-widest px-4 py-2 hover:bg-red-500/10 disabled:opacity-50"
                  >
                    Reject…
                  </button>
                  <Link
                    to={`/admin/voices/${item.id}`}
                    className="font-mono text-xs uppercase tracking-widest text-ink-2 underline self-center"
                  >
                    Open in editor
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageShell>
  );
}

export function VoicesQueue() {
  return (
    <AdminGuard allow={["admin", "editor"]}>
      <VoicesQueueInner />
    </AdminGuard>
  );
}
